import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Session } from '@supabase/supabase-js';
import clsx from 'clsx';
import { config } from '../lib/config';
import { MATCH_COPY_SPORT_OPTIONS, MATCH_COPY_VARIANT_OPTIONS } from '../shared/matchCopyContext';
import { normalizeSportId as normalizeAppSportId } from '../shared/sports';
import {
  getScoreChallengeVariantOptions,
  getTrickStyleVariantOptionsForPlayMode,
  isGymSport,
  isScoreChallengeOnlySport,
  isTrickStyleSport,
  shouldShowGenericScoreChallengeSubtypePicker,
  supportsScoreChallengeMode,
} from '../shared/scoreChallenge';

type StatusTone = 'info' | 'success' | 'error' | 'warning';
type PreviewViewport = 'phone' | 'tablet' | 'desktop';
type PreviewLobbyState = 'config' | 'ready';
type PreviewMode = 'quick_play' | 'teams' | 'individuals' | 'score_challenge';
type PreviewStep = 'lobby' | 'game_on' | 'round_complete' | 'series_decision' | 'post_match';

type StatusMessage = {
  tone: StatusTone;
  text: string;
};

type PreviewMessage =
  | {
      source: 'rvlry-admin-preview';
      type: 'preview-admin-auth';
      payload: {
        accessToken: string;
      };
    }
  | {
      source: 'rvlry-admin-preview';
      type: 'sport-format-audit-preview:update';
      payload: {
        sportId: string;
        mode: PreviewMode;
        variant: string | null;
        viewport: PreviewViewport;
        lobby: PreviewLobbyState;
        step: PreviewStep;
        players: number;
        openPicker: boolean;
      };
    };

const PREVIEW_MODES: Array<{ value: PreviewMode; label: string }> = [
  { value: 'quick_play', label: 'Head to Head' },
  { value: 'teams', label: 'Teams' },
  { value: 'individuals', label: 'Free-for-All' },
  { value: 'score_challenge', label: 'Solo' },
];

const PREVIEW_STEPS: Array<{ value: PreviewStep; label: string; badge: string }> = [
  { value: 'lobby', label: 'Lobby', badge: 'Lobby' },
  { value: 'game_on', label: 'Play On', badge: 'Play On' },
  { value: 'round_complete', label: 'Round Over', badge: 'Round Over' },
  { value: 'series_decision', label: 'What Next', badge: 'What Next' },
  { value: 'post_match', label: 'Results', badge: 'Results' },
];

const DEFAULT_PREVIEW_ORIGIN = 'https://play.rvlryapp.com';
const DEFAULT_PLAYER_COUNT = 4;
const MAX_PLAYER_COUNT = 8;
const MIN_PLAYER_COUNT = 1;

function normalizeViewport(value: unknown): PreviewViewport {
  return value === 'tablet' || value === 'desktop' ? value : 'phone';
}

function normalizeLobbyState(value: unknown): PreviewLobbyState {
  return value === 'ready' ? 'ready' : 'config';
}

function normalizeStep(value: unknown): PreviewStep {
  return value === 'game_on' ||
    value === 'round_complete' ||
    value === 'series_decision' ||
    value === 'post_match'
    ? value
    : 'lobby';
}

function normalizeMode(value: unknown): PreviewMode {
  return value === 'teams' || value === 'individuals' || value === 'score_challenge'
    ? value
    : 'quick_play';
}

function normalizePlayerCount(value: unknown) {
  const numeric = typeof value === 'string' ? Number.parseInt(value, 10) : typeof value === 'number' ? value : Number.NaN;
  if (!Number.isFinite(numeric)) return DEFAULT_PLAYER_COUNT;
  return Math.min(MAX_PLAYER_COUNT, Math.max(MIN_PLAYER_COUNT, Math.trunc(numeric)));
}

function normalizeSportId(value: unknown) {
  const raw = typeof value === 'string' ? value.trim() : '';
  return MATCH_COPY_SPORT_OPTIONS.some((option) => option.value === raw) ? raw : MATCH_COPY_SPORT_OPTIONS[0]?.value ?? 'skateboarding';
}

function normalizeVariant(value: unknown) {
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;
}

function getAvailableModes(sportId: string): PreviewMode[] {
  const resolvedSportId = normalizeAppSportId(sportId);
  if (isScoreChallengeOnlySport(resolvedSportId)) return ['score_challenge'];
  const modes: PreviewMode[] = ['quick_play', 'teams', 'individuals'];
  if (supportsScoreChallengeMode(resolvedSportId)) modes.push('score_challenge');
  return modes;
}

function getAvailableVariants(sportId: string, mode: PreviewMode) {
  const resolvedSportId = normalizeAppSportId(sportId);
  if (isTrickStyleSport(resolvedSportId)) {
    return getTrickStyleVariantOptionsForPlayMode(resolvedSportId, mode);
  }
  if (mode === 'score_challenge') {
    const hasLobbySubtypePicker =
      resolvedSportId === 'pinball' ||
      isGymSport(resolvedSportId) ||
      shouldShowGenericScoreChallengeSubtypePicker(resolvedSportId);
    if (!hasLobbySubtypePicker) return [];
    return getScoreChallengeVariantOptions(resolvedSportId);
  }
  return MATCH_COPY_VARIANT_OPTIONS[sportId] ?? [];
}

function ensureNoIndexMeta() {
  if (typeof document === 'undefined') return () => {};
  const existing = document.querySelector('meta[name="robots"]') as HTMLMetaElement | null;
  const prior = existing?.content ?? null;
  const tag = existing ?? document.createElement('meta');
  tag.setAttribute('name', 'robots');
  tag.setAttribute('content', 'noindex,nofollow');
  if (!existing) document.head.appendChild(tag);
  return () => {
    if (!existing) {
      tag.remove();
      return;
    }
    if (prior != null) existing.setAttribute('content', prior);
  };
}

function createPlayPreviewOrigin() {
  if (typeof window === 'undefined') return DEFAULT_PREVIEW_ORIGIN;
  const params = new URLSearchParams(window.location.search);
  const overridden = params.get('playPreviewOrigin');
  if (overridden) return overridden.replace(/\/$/, '');
  return DEFAULT_PREVIEW_ORIGIN;
}

export default function SportFormatAuditWorkspace({ session }: { session: Session }) {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [status, setStatus] = useState<StatusMessage | null>(null);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [iframeError, setIframeError] = useState<string | null>(null);
  const [sportId, setSportId] = useState<string>(() => normalizeSportId('skateboarding'));
  const [mode, setMode] = useState<PreviewMode>('quick_play');
  const [viewport, setViewport] = useState<PreviewViewport>('phone');
  const [lobbyState, setLobbyState] = useState<PreviewLobbyState>('config');
  const [step, setStep] = useState<PreviewStep>('lobby');
  const [playerCount, setPlayerCount] = useState(DEFAULT_PLAYER_COUNT);
  const [openPicker, setOpenPicker] = useState(false);
  const [variant, setVariant] = useState<string | null>(null);

  const previewOrigin = useMemo(() => createPlayPreviewOrigin(), []);
  const availableModes = useMemo(() => getAvailableModes(sportId), [sportId]);
  const availableVariants = useMemo(
    () => getAvailableVariants(sportId, mode),
    [mode, sportId],
  );

  useEffect(() => ensureNoIndexMeta(), []);

  useEffect(() => {
    if (!availableModes.includes(mode)) {
      setMode(availableModes[0] ?? 'quick_play');
    }
  }, [availableModes, mode]);

  useEffect(() => {
    if (availableVariants.length === 0) {
      if (variant !== null) setVariant(null);
      return;
    }
    if (!variant || !availableVariants.some((option) => option.value === variant)) {
      setVariant(availableVariants[0]?.value ?? null);
    }
  }, [availableVariants, variant]);

  const previewUrl = useMemo(() => {
    const query = new URLSearchParams({
      sportId,
      mode,
      viewport,
      lobby: lobbyState,
      step,
      players: String(playerCount),
      open: openPicker ? '1' : '0',
    });
    if (variant) query.set('variant', variant);
    return `${previewOrigin}/preview/sport-format-audit?${query.toString()}`;
  }, [lobbyState, mode, openPicker, playerCount, previewOrigin, sportId, step, variant, viewport]);

  const previewSummary = useMemo(() => {
    const sportLabel = MATCH_COPY_SPORT_OPTIONS.find((option) => option.value === sportId)?.label ?? sportId;
    const modeLabel = PREVIEW_MODES.find((option) => option.value === mode)?.label ?? mode;
    const variantLabel = availableVariants.find((option) => option.value === variant)?.label ?? null;
    const stepLabel = PREVIEW_STEPS.find((option) => option.value === step)?.label ?? step;
    return [sportLabel, modeLabel, variantLabel, stepLabel, `${playerCount} player${playerCount === 1 ? '' : 's'}`]
      .filter(Boolean)
      .join(' · ');
  }, [availableVariants, mode, playerCount, sportId, step, variant]);

  const pushPreviewState = useCallback(() => {
    const target = iframeRef.current?.contentWindow;
    if (!target) return;
    const authMessage: PreviewMessage = {
      source: 'rvlry-admin-preview',
      type: 'preview-admin-auth',
      payload: {
        accessToken: session.access_token,
      },
    };
    const stateMessage: PreviewMessage = {
      source: 'rvlry-admin-preview',
      type: 'sport-format-audit-preview:update',
      payload: {
        sportId,
        mode,
        variant,
        viewport,
        lobby: lobbyState,
        step,
        players: playerCount,
        openPicker,
      },
    };
    target.postMessage(authMessage, previewOrigin);
    target.postMessage(stateMessage, previewOrigin);
  }, [lobbyState, mode, openPicker, playerCount, previewOrigin, session.access_token, sportId, step, variant, viewport]);

  useEffect(() => {
    if (!iframeLoaded) return;
    const timer = window.setTimeout(() => pushPreviewState(), 80);
    return () => window.clearTimeout(timer);
  }, [iframeLoaded, pushPreviewState]);

  useEffect(() => {
    setIframeLoaded(false);
    setIframeError(null);
  }, [previewUrl]);

  const openInNewTab = useCallback(() => {
    window.open(previewUrl, '_blank', 'noopener,noreferrer');
  }, [previewUrl]);

  const copyPreviewUrl = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(previewUrl);
      setStatus({ tone: 'success', text: 'Preview URL copied.' });
    } catch {
      setStatus({ tone: 'warning', text: 'Copy failed. Use Open in new tab instead.' });
    }
  }, [previewUrl]);

  const refreshPreview = useCallback(() => {
    setIframeLoaded(false);
    setIframeError(null);
    if (iframeRef.current) iframeRef.current.src = previewUrl;
  }, [previewUrl]);

  return (
    <div className="admin-page cms-workspace match-copy-workspace">
      <div className="admin-page-head">
        <div>
          <div className="admin-h1">Sport format audit</div>
          <div className="admin-subtitle">
            Set the scenario, then review the live screen. Copy the URL to test it on phone.
          </div>
        </div>
        <div className="admin-actions">
          <button className="admin-btn" type="button" onClick={copyPreviewUrl}>
            Copy URL
          </button>
          <button className="admin-btn" type="button" onClick={openInNewTab}>
            Open in new tab
          </button>
          <button className="admin-btn primary" type="button" onClick={refreshPreview}>
            Refresh preview
          </button>
        </div>
      </div>

      {status?.text ? <div className={clsx('admin-status', status.tone)}>{status.text}</div> : null}

      <div className="cms-layout">
        <aside className="cms-editor-pane match-copy-editor-pane">
          <div className="cms-page-context cms-soft-card">
            <div className="cms-page-context-head">
              <div>
                <div className="cms-editor-form-kicker">Scenario</div>
                <div className="admin-muted">Adjust the setup, then compare it in the live preview.</div>
              </div>
              <div className="cms-page-context-route">{config.hubActiveEnv}</div>
            </div>

            <div className="cms-editor-form-section" style={{ marginTop: 12 }}>
              <div className="match-copy-settings-grid">
                <label className="admin-label">
                  <span>Sport</span>
                  <select
                    className="admin-input"
                    value={sportId}
                    onChange={(event) => setSportId(event.target.value)}
                  >
                    {MATCH_COPY_SPORT_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="admin-label">
                  <span>Mode</span>
                  <select
                    className="admin-input"
                    value={mode}
                    onChange={(event) => setMode(normalizeMode(event.target.value))}
                  >
                    {availableModes.map((value) => {
                      const option = PREVIEW_MODES.find((entry) => entry.value === value);
                      if (!option) return null;
                      return (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      );
                    })}
                  </select>
                </label>

                <label className="admin-label">
                  <span>Challenge type</span>
                  <select
                    className="admin-input"
                    value={variant ?? ''}
                    onChange={(event) => setVariant(normalizeVariant(event.target.value))}
                    disabled={availableVariants.length === 0}
                  >
                    <option value="">None</option>
                    {availableVariants.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="admin-label">
                  <span>Match step</span>
                  <select
                    className="admin-input"
                    value={step}
                    onChange={(event) => setStep(normalizeStep(event.target.value))}
                  >
                    {PREVIEW_STEPS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="admin-label">
                  <span>Lobby state</span>
                  <select
                    className="admin-input"
                    value={lobbyState}
                    onChange={(event) => setLobbyState(normalizeLobbyState(event.target.value))}
                  >
                    <option value="config">Config</option>
                    <option value="ready">Ready</option>
                  </select>
                </label>

                <label className="admin-label">
                  <span>Players</span>
                  <input
                    className="admin-input"
                    type="number"
                    min={MIN_PLAYER_COUNT}
                    max={MAX_PLAYER_COUNT}
                    step={1}
                    value={playerCount}
                    onChange={(event) => setPlayerCount(normalizePlayerCount(event.target.value))}
                  />
                </label>
              </div>

              <label className="admin-label" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginTop: 14 }}>
                <span>Start with picker open</span>
                <input
                  type="checkbox"
                  checked={openPicker}
                  onChange={(event) => setOpenPicker(event.target.checked)}
                />
              </label>
            </div>
          </div>
        </aside>

        <div className="cms-stage">
          <div className="cms-stage-toolbar">
            <div className="sport-audit-preview-meta">
              <div className="admin-section-title">Live preview</div>
              <div className="cms-preview-status">{previewSummary}</div>
              <div className="sport-audit-preview-badges">
                <span className="admin-badge">{PREVIEW_STEPS.find((option) => option.value === step)?.badge ?? 'Lobby'}</span>
                <span className="admin-badge">{lobbyState === 'ready' ? 'Lobby ready' : 'Lobby config'}</span>
                <span className="admin-badge">{playerCount} seeded user{playerCount === 1 ? '' : 's'}</span>
                {openPicker ? <span className="admin-badge">Picker opens on load</span> : null}
              </div>
            </div>
            <div className="cms-stage-header-actions">
              <div className="admin-segmented" aria-label="Viewport">
                {(['phone', 'tablet', 'desktop'] as const).map((option) => (
                  <button
                    key={option}
                    className={clsx('admin-seg', viewport === option && 'active')}
                    type="button"
                    onClick={() => setViewport(option)}
                  >
                    {option[0].toUpperCase() + option.slice(1)}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="cms-stage-canvas">
            <div className={clsx('match-copy-preview-frame', 'iframe-shell', viewport)}>
              {!iframeLoaded ? <div className="cms-preview-overlay">Loading preview…</div> : null}
              {iframeError ? <div className="cms-preview-overlay error">{iframeError}</div> : null}
              <iframe
                ref={iframeRef}
                title="Sport format audit preview"
                src={previewUrl}
                className="cms-match-preview-iframe"
                onLoad={() => {
                  setIframeLoaded(true);
                  setIframeError(null);
                }}
                onError={() => {
                  setIframeLoaded(false);
                  setIframeError('Unable to load the sport format preview route.');
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
